import { API_BASE_URL } from "../config/api";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import PhoneActions from "../components/PhoneActions";
import { getPrimaryCustomerContact } from "../utils/customerForm";

const formatLocation = (address) => {
  if (!address) return "-";
  if (typeof address === "string") return address || "-";
  return [address.city, address.state].filter(Boolean).join(", ")
    || [address.street, address.area, address.city].filter(Boolean).join(", ")
    || "-";
};

const getPrimaryContactLabel = (customer) => {
  const primaryContact = getPrimaryCustomerContact(customer);
  if (!primaryContact) return "-";
  const pieces = [primaryContact.role, primaryContact.name, primaryContact.mobileNumber].filter(Boolean);
  return pieces.join(" · ") || "-";
};

function InfoCard({ label, value, compact = false }) {
  return (
    <div className={`rounded-xl border border-slate-100 bg-slate-50/50 ${compact ? "p-3" : "p-4"}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <div className={`mt-1 text-sm text-slate-700 ${compact ? "leading-5" : "leading-6"}`}>{value}</div>
    </div>
  );
}

function CustomerMobileCard({ customer, onOpen, onRemove, canManage }) {
  return (
    <article className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-100 cursor-pointer" onClick={onOpen}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
        {customer.customerId || "Customer"}
      </p>
      <h2 className="mt-2 text-lg font-bold leading-snug text-slate-900">{customer.name || "-"}</h2>
      <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Primary Contact</p>
            <p className="mt-1 text-sm text-slate-700">{getPrimaryCustomerContact(customer)?.name || customer.contactPerson || "-"}</p>
            <div className="mt-2 text-sm text-slate-700">
              <PhoneActions phone={customer.phone || ""} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold uppercase tracking-[0.18em] text-slate-500">Additional Info</span>
        <span className="truncate text-slate-700">{customer.additionalInfo || "-"}</span>
      </div>
      {canManage && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(customer);
            }}
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100"
          >
            Remove from Group
          </button>
        </div>
      )}
    </article>
  );
}

export default function CustomerGroupProfile() {
  const { id } = useParams();
  const role = localStorage.getItem("role") || "";
  const location = useLocation();
  const navigate = useNavigate();

  const isEngineerRoute = location.pathname.startsWith("/engineer");
  const isEngineerView = role === "engineer" && isEngineerRoute;
  const isAccountsView = role === "accounts";
  const canManage = role === "admin" || role === "accounts";

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAllMobile, setShowAllMobile] = useState(false);
  const [showAllDesktop, setShowAllDesktop] = useState(false);

  // States for adding member customer
  const [allCustomers, setAllCustomers] = useState([]);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [modalSearchQuery, setModalSearchQuery] = useState("");

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_BASE_URL + `/api/customer-groups/${id}`);
        setGroup(res.data);
      } catch (err) {
        console.error("Failed to load customer group details", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroupData();
  }, [id]);

  useEffect(() => {
    setShowAllMobile(false);
    setShowAllDesktop(false);
  }, [search]);

  useEffect(() => {
    if (showAddMemberModal) {
      const fetchAllCustomers = async () => {
        try {
          const res = await axios.get(API_BASE_URL + "/api/customers");
          const cData = res.data;
          setAllCustomers(Array.isArray(cData) ? cData : (cData.customers || []));
        } catch (e) {
          console.error("Failed to fetch all customers", e);
        }
      };
      fetchAllCustomers();
    }
  }, [showAddMemberModal]);

  const eligibleCustomers = useMemo(() => {
    const list = allCustomers.filter(c => String(c.groupId?._id || c.groupId) !== String(id));
    if (!modalSearchQuery.trim()) return list;
    const query = modalSearchQuery.toLowerCase();
    return list.filter(c => 
      c.name.toLowerCase().includes(query) || 
      (c.customerId && c.customerId.toLowerCase().includes(query))
    );
  }, [allCustomers, id, modalSearchQuery]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedCustomerId) return;
    try {
      await axios.post(API_BASE_URL + "/api/customer-groups/move", {
        customerId: selectedCustomerId,
        groupId: id
      });
      alert("Customer added to group successfully.");
      setShowAddMemberModal(false);
      setSelectedCustomerId("");
      setModalSearchQuery("");
      
      const res = await axios.get(API_BASE_URL + `/api/customer-groups/${id}`);
      setGroup(res.data);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to add customer to group");
    }
  };

  const handleRemoveMember = async (customer) => {
    if (!window.confirm(`Are you sure you want to remove "${customer.name}" from this group?`)) {
      return;
    }
    try {
      await axios.post(API_BASE_URL + "/api/customer-groups/move", {
        customerId: customer._id,
        groupId: null
      });
      alert("Customer removed from group successfully.");
      const res = await axios.get(API_BASE_URL + `/api/customer-groups/${id}`);
      setGroup(res.data);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to remove customer from group");
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!group || !Array.isArray(group.customers)) return [];
    const query = search.trim().toLowerCase();
    return group.customers.filter((c) =>
      [c.customerId, c.name, c.email, c.contactPerson, c.phone, formatLocation(c.address)]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [group, search]);

  const handleBack = () => {
    navigate(
      isEngineerView
        ? "/engineer/customer-groups"
        : isAccountsView
        ? "/accounts/customer-groups"
        : "/customer-groups"
    );
  };

  const handleViewCustomer = (customerId) => {
    navigate(
      isEngineerView
        ? `/engineer/customer/${customerId}`
        : isAccountsView
        ? `/accounts/customer/${customerId}`
        : `/customer/${customerId}`
    );
  };

  const mobileCustomers = showAllMobile ? filteredCustomers : filteredCustomers.slice(0, 7);
  const desktopCustomers = showAllDesktop ? filteredCustomers : filteredCustomers.slice(0, 12);

  if (loading) {
    const loader = <div className="p-8 text-center text-sm text-slate-500">Loading group details...</div>;
    return isEngineerView ? <EngineerLayout>{loader}</EngineerLayout> : loader;
  }

  if (!group) {
    const errorMsg = (
      <div className="p-8 text-center">
        <p className="text-sm text-red-500 font-semibold">Customer Group not found.</p>
        <button onClick={handleBack} className="mt-4 rounded bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800">
          Back to Groups
        </button>
      </div>
    );
    return isEngineerView ? <EngineerLayout>{errorMsg}</EngineerLayout> : errorMsg;
  }

  const content = (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Header banner */}
      <div className="border-b border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[95%] xl:max-w-[1600px] flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50"
              aria-label="Back to customer groups"
            >
              <span className="text-xl leading-none">&larr;</span>
            </button>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">Customer Group Profile</p>
              <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">{group.groupName}</h1>
              <p className="mt-1 text-sm text-slate-500">{group.description || "No description provided."}</p>
            </div>
          </div>
          {(role === "admin" || role === "accounts") && (
            <button
              type="button"
              onClick={() => navigate(
                isEngineerView
                  ? `/engineer/customer-groups/${id}/quotations`
                  : isAccountsView
                  ? `/accounts/customer-groups/${id}/quotations`
                  : `/customer-groups/${id}/quotations`
              )}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-[6px] bg-sky-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700 whitespace-nowrap self-start sm:self-center"
            >
              <span className="material-symbols-outlined text-lg">description</span>
              Quotations
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-[95%] xl:max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        {/* Bento Grid Metrics Dashboard */}
        <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_#eff6ff,_#dbeafe)] p-5 shadow-sm">
            <span className="material-symbols-outlined text-blue-600 text-3xl">corporate_fare</span>
            <p className="mt-2 text-2xl font-extrabold text-blue-950">{group.totalCustomers || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-blue-700/80 mt-1">Total Customers</p>
          </div>
          
          <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_#fef3c7,_#fde68a)] p-5 shadow-sm">
            <span className="material-symbols-outlined text-amber-600 text-3xl">pending_actions</span>
            <p className="mt-2 text-2xl font-extrabold text-amber-950">{group.openComplaints || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-amber-700/80 mt-1">Open Complaints</p>
          </div>
          
          <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_#ecfdf5,_#d1fae5)] p-5 shadow-sm">
            <span className="material-symbols-outlined text-emerald-600 text-3xl">task_alt</span>
            <p className="mt-2 text-2xl font-extrabold text-emerald-950">{group.completedComplaints || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700/80 mt-1">Completed Complaints</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_#faf5ff,_#f3e8ff)] p-5 shadow-sm">
            <span className="material-symbols-outlined text-purple-600 text-3xl">precision_manufacturing</span>
            <p className="mt-2 text-2xl font-extrabold text-purple-950">{group.machines || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-purple-700/80 mt-1">Unique Machines</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,_#fdf2f8,_#fce7f3)] p-5 shadow-sm col-span-2 sm:col-span-1">
            <span className="material-symbols-outlined text-pink-600 text-3xl">engineering</span>
            <p className="mt-2 text-2xl font-extrabold text-pink-950">{group.engineersVisited || 0}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-pink-700/80 mt-1">Visited Engineers</p>
          </div>
        </section>

        {/* Directory Search & Filter */}
        <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-bold text-slate-900">Group Members ({filteredCustomers.length})</h2>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="relative w-full sm:w-72">
                <input
                  type="text"
                  placeholder="Search group members..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-10 w-full rounded-[6px] border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-lg">search</span>
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCustomerId("");
                    setModalSearchQuery("");
                    setShowAddMemberModal(true);
                  }}
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-[6px] bg-blue-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 whitespace-nowrap"
                >
                  <span className="material-symbols-outlined text-lg">person_add</span>
                  Add Member
                </button>
              )}
            </div>
          </div>

          <div className="mt-6">
            {filteredCustomers.length === 0 ? (
              <p className="text-center text-sm text-slate-500 py-6">No member accounts match the search criteria.</p>
            ) : (
              <>
                {/* Mobile Cards */}
                <div className="space-y-3 lg:hidden">
                  {mobileCustomers.map((customer) => (
                    <CustomerMobileCard
                      key={customer._id}
                      customer={customer}
                      onOpen={() => handleViewCustomer(customer._id)}
                      onRemove={handleRemoveMember}
                      canManage={canManage}
                    />
                  ))}
                  {filteredCustomers.length > 7 && (
                    <button
                      type="button"
                      onClick={() => setShowAllMobile(c => !c)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm"
                    >
                      {showAllMobile ? "Show Less" : `View More (${filteredCustomers.length - 7})`}
                    </button>
                  )}
                </div>

                {/* Desktop Grid Layout */}
                <div className="hidden lg:grid grid-cols-1 gap-4 xl:grid-cols-2">
                  {desktopCustomers.map((customer) => (
                    <article
                      key={customer._id}
                      className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                            {customer.customerId || "Customer"}
                          </p>
                          <h2 className="mt-1 truncate whitespace-nowrap text-lg font-bold text-slate-900">
                            {customer.name || "-"}
                          </h2>
                          <p className="mt-0.5 text-xs text-slate-500">{formatLocation(customer.address)}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(customer)}
                              className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                            >
                              Remove
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleViewCustomer(customer._id)}
                            className="rounded-xl bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                          >
                            View Profile
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <InfoCard compact label="Primary Contact" value={getPrimaryContactLabel(customer)} />
                        <InfoCard compact label="Phone" value={<PhoneActions phone={customer.phone || ""} />} />
                        <InfoCard compact label="Email" value={customer.email || "-"} />
                        <InfoCard compact label="Additional Info" value={customer.additionalInfo || "-"} />
                      </div>
                    </article>
                  ))}
                  {filteredCustomers.length > 12 && (
                    <button
                      type="button"
                      onClick={() => setShowAllDesktop(c => !c)}
                      className="col-span-full rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      {showAllDesktop ? "Show Less" : `View More (${filteredCustomers.length - 12})`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {/* ADD MEMBER MODAL */}
      {showAddMemberModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between pb-3 border-b border-slate-150">
              <h3 className="text-lg font-bold text-slate-950">Add Customer to Group</h3>
              <button 
                type="button"
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedCustomerId("");
                  setModalSearchQuery("");
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <p className="mt-2 text-xs text-slate-500">
              Select an existing customer to add to <strong>{group.groupName}</strong>. If they are already in another group, they will be moved to this group.
            </p>

            <div className="mt-4 relative">
              <input
                type="text"
                placeholder="Search customers by name or ID..."
                value={modalSearchQuery}
                onChange={(e) => setModalSearchQuery(e.target.value)}
                className="h-10 w-full rounded-[6px] border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-lg">search</span>
            </div>

            <div className="mt-3 flex-1 overflow-y-auto min-h-[200px] max-h-[350px] border border-slate-150 rounded-[6px] divide-y divide-slate-100">
              {eligibleCustomers.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  No customers found.
                </div>
              ) : (
                eligibleCustomers.map((cust) => {
                  const isSelected = selectedCustomerId === cust._id;
                  const currentGroupName = cust.groupId?.groupName || "No Group";
                  return (
                    <button
                      key={cust._id}
                      type="button"
                      onClick={() => setSelectedCustomerId(cust._id)}
                      className={`w-full text-left p-3 text-sm transition flex items-center justify-between gap-3 ${
                        isSelected 
                          ? "bg-blue-50 text-blue-900 font-semibold" 
                          : "hover:bg-slate-50 text-slate-700"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-bold truncate">{cust.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{cust.customerId || "No ID"}</div>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          cust.groupId ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
                        }`}>
                          {currentGroupName}
                        </span>
                        {isSelected && (
                          <span className="material-symbols-outlined text-blue-600 text-lg">check_circle</span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-150 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedCustomerId("");
                  setModalSearchQuery("");
                }}
                className="rounded-[6px] border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedCustomerId}
                onClick={handleAddMember}
                className={`rounded-[6px] px-4 py-2 text-sm font-bold text-white shadow-sm transition ${
                  selectedCustomerId 
                    ? "bg-blue-600 hover:bg-blue-700" 
                    : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                + Add to Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return isEngineerView ? <EngineerLayout>{content}</EngineerLayout> : content;
}
